/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, type ComponentType } from "react";
import { motion } from "motion/react";
import { ArrowDown, ShieldCheck } from "lucide-react";
import {
	PageHeader,
	RevealOnScroll,
	SectionDivider,
	SectionWrapper,
} from "./SpecialProjectsPage.primitives";
import ProjectCard from "./customCopmonent/ProjectCard";

type AssuranceItem = {
	title: string;
	text: string;
	icon: ComponentType<{ className?: string }>;
};

type Project = {
	title: string;
	location?: string;
	image?: string;
	sector?: string;
	specs?: string[];
	stats?: string[];
};

type SpecialProjectsPageProps = {
	data: {
		title: string;
		description: string;
		meta?: string[];
		projects?: Project[];
		assurance?: AssuranceItem[];
	};
};

const containerVariants = {
	hidden: {},
	visible: {
		transition: {
			staggerChildren: 0.12,
			delayChildren: 0.08,
		},
	},
};

export default function SpecialProjectsPage({
	data,
}: SpecialProjectsPageProps) {
	const { title, description, meta = [], projects = [], assurance = [] } = data;

	return (
		<>
			<SectionWrapper
				id="special-projects"
				className="pt-0"
				contentClassName="max-w-7xl px-6 py-12">
				<motion.div
					id="projects-list"
					variants={containerVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: "-120px" }}
					className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{projects.map((project, index) => (
						<ProjectCard
							key={`${project.title}-${index}`}
							project={project}
							index={index}
						/>
					))}
				</motion.div>
			</SectionWrapper>

			<SectionDivider />

			{assurance.length > 0 ?
				<SectionWrapper className="pt-0">
					<div className="grid gap-5 md:grid-cols-3">
						{assurance.map((item, index) => {
							const Icon = item.icon || ShieldCheck;

							return (
								<RevealOnScroll
									key={item.title}
									delay={index * 0.06}>
									<div className="h-full rounded-2xl border border-slate-200/80 bg-slate-50 p-6 shadow-sm">
										<div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#2d2f92]/8 text-[#2d2f92]">
											<Icon className="h-5 w-5" />
										</div>
										<h3 className="mt-4 text-lg font-semibold text-slate-900">
											{item.title}
										</h3>
										<p className="mt-2 text-sm leading-7 text-slate-600">
											{item.text}
										</p>
									</div>
								</RevealOnScroll>
							);
						})}
					</div>
				</SectionWrapper>
			:	null}
		</>
	);
}
